/**
 * About Hero - 3D Scroll-Morphing Typography
 * Future-forward luxury hero with Three.js morphing text and particle dissolve
 */

import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.js';
import { FontLoader } from 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/loaders/FontLoader.js';
import { TextGeometry } from 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/geometries/TextGeometry.js';

class AboutHero3D {
  constructor() {
    this.container = document.querySelector('.about-hero-3d');
    if (!this.container) return;
    
    this.canvas = this.container.querySelector('.hero-3d-canvas');
    this.scrollProgress = 0;
    this.particles = [];
    this.textMesh = null;
    this.isDissolving = false;
    this.is3DReady = false;
    
    // Check for reduced motion preference
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (this.prefersReducedMotion) {
      this.initStaticVersion();
    } else if (this.isMobile()) {
      this.init2DVersion();
    } else {
      // Start 3D loading immediately but keep fallback visible
      this.init3DVersion();
    }
  }
  
  isMobile() {
    return window.innerWidth < 768;
  }
  
  /**
   * Static version for reduced motion preference
   */
  initStaticVersion() {
    this.container.classList.add('hero-static');
    const textElement = this.container.querySelector('.hero-3d-text');
    if (textElement) {
      textElement.style.opacity = '1';
      textElement.style.transform = 'none';
    }
  }
  
  /**
   * 2D CSS version for mobile
   */
  init2DVersion() {
    this.container.classList.add('hero-2d');
    this.init2DAnimations();
  }
  
  init2DAnimations() {
    const textElement = this.container.querySelector('.hero-3d-text');
    if (!textElement) return;
    
    // Kinetic typography animation using CSS
    const text = textElement.textContent;
    const letters = text.split('');
    textElement.innerHTML = '';
    
    letters.forEach((letter, index) => {
      const span = document.createElement('span');
      span.textContent = letter;
      span.style.animationDelay = `${index * 0.05}s`;
      span.classList.add('letter-animate');
      textElement.appendChild(span);
    });
    
    // Scroll-based fade
    window.addEventListener('scroll', () => {
      const progress = Math.min(window.scrollY / window.innerHeight, 1);
      textElement.style.opacity = 1 - progress;
      textElement.style.transform = `translateY(${progress * 100}px)`;
    }, { passive: true });
  }
  
  /**
   * Full 3D version for desktop
   */
  init3DVersion() {
    this.setupScene();
    this.setupCamera();
    this.setupRenderer();
    this.setupLights();
    this.loadFont();
    this.setupScrollListener();
    this.animate();
  }
  
  setupScene() {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xFFFDFB);
  }
  
  setupCamera() {
    this.camera = new THREE.PerspectiveCamera(
      75,
      this.container.offsetWidth / this.container.offsetHeight,
      0.1,
      1000
    );
    this.camera.position.z = 5;
  }
  
  setupRenderer() {
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true
    });
    this.renderer.setSize(this.container.offsetWidth, this.container.offsetHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    
    window.addEventListener('resize', () => this.onResize(), false);
  }
  
  setupLights() {
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(ambientLight);
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(5, 5, 5);
    this.scene.add(directionalLight);
    
    const pointLight = new THREE.PointLight(0xEC9A6F, 1, 100);
    pointLight.position.set(-5, 5, 5);
    this.scene.add(pointLight);
  }
  
  loadFont() {
    const loader = new FontLoader();
    loader.load(
      'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/fonts/helvetiker_bold.typeface.json',
      (font) => this.createText(font),
      undefined,
      (error) => {
        console.error('Font loading error:', error);
        this.init2DVersion(); // Fallback to 2D
      }
    );
  }
  
  createText(font) {
    const textGeometry = new TextGeometry('VYUDU', {
      font: font,
      size: 1,
      height: 0.3,
      curveSegments: 12,
      bevelEnabled: true,
      bevelThickness: 0.03,
      bevelSize: 0.02,
      bevelOffset: 0,
      bevelSegments: 5
    });
    
    textGeometry.center();
    
    // Gradient material
    const material = new THREE.MeshStandardMaterial({
      color: 0xEC9A6F,
      metalness: 0.3,
      roughness: 0.4,
      emissive: 0xEC9A6F,
      emissiveIntensity: 0.2
    });
    
    this.textMesh = new THREE.Mesh(textGeometry, material);
    this.scene.add(this.textMesh);
    
    // Create particle system from text vertices
    this.createParticlesFromText(textGeometry);
    
    // Mark 3D as ready and trigger transition
    this.mark3DReady();
  }
  
  createParticlesFromText(geometry) {
    const vertices = geometry.attributes.position.array;
    const particleGeometry = new THREE.BufferGeometry();
    const particlePositions = [];
    const particleColors = [];
    
    // Sample vertices for particles
    for (let i = 0; i < vertices.length; i += 9) {
      particlePositions.push(vertices[i], vertices[i + 1], vertices[i + 2]);
      
      // Orange to gold gradient
      const color = new THREE.Color();
      color.setHSL(0.08 + Math.random() * 0.05, 0.7, 0.6);
      particleColors.push(color.r, color.g, color.b);
    }
    
    particleGeometry.setAttribute('position', new THREE.Float32BufferAttribute(particlePositions, 3));
    particleGeometry.setAttribute('color', new THREE.Float32BufferAttribute(particleColors, 3));
    
    const particleMaterial = new THREE.PointsMaterial({
      size: 0.05,
      vertexColors: true,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending
    });
    
    this.particleSystem = new THREE.Points(particleGeometry, particleMaterial);
    this.scene.add(this.particleSystem);
  }
  
  setupScrollListener() {
    window.addEventListener('scroll', () => {
      const scrollY = window.scrollY;
      const heroHeight = this.container.offsetHeight;
      this.scrollProgress = Math.min(scrollY / heroHeight, 1);
      
      // Trigger dissolve at 30% scroll
      if (this.scrollProgress > 0.3 && !this.isDissolving) {
        this.isDissolving = true;
      }
    }, { passive: true });
  }
  
  mark3DReady() {
    // Wait a moment for first render, then show 3D
    setTimeout(() => {
      this.is3DReady = true;
      this.container.classList.add('hero-3d-ready');
      this.container.classList.remove('loading');
    }, 100);
  }
  
  animate() {
    requestAnimationFrame(() => this.animate());
    
    if (this.textMesh) {
      // Subtle morphing animation
      const time = Date.now() * 0.001;
      this.textMesh.rotation.y = Math.sin(time * 0.3) * 0.1;
      this.textMesh.position.y = Math.sin(time * 0.5) * 0.05;
      
      // Dissolve effect based on scroll
      if (this.isDissolving) {
        const dissolveProgress = Math.min((this.scrollProgress - 0.3) / 0.3, 1);
        
        // Fade out text
        this.textMesh.material.opacity = 1 - dissolveProgress;
        this.textMesh.material.transparent = true;
        
        // Fade in and scatter particles
        this.particleSystem.material.opacity = dissolveProgress;
        this.particleSystem.rotation.y += 0.002;
        
        // Scatter particles
        const positions = this.particleSystem.geometry.attributes.position.array;
        for (let i = 0; i < positions.length; i += 3) {
          positions[i] += (Math.random() - 0.5) * 0.01 * dissolveProgress;
          positions[i + 1] += (Math.random() - 0.5) * 0.01 * dissolveProgress;
          positions[i + 2] += (Math.random() - 0.5) * 0.01 * dissolveProgress;
        }
        this.particleSystem.geometry.attributes.position.needsUpdate = true;
      }
    }
    
    this.renderer.render(this.scene, this.camera);
  }
  
  onResize() {
    this.camera.aspect = this.container.offsetWidth / this.container.offsetHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(this.container.offsetWidth, this.container.offsetHeight);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new AboutHero3D());
} else {
  new AboutHero3D();
}

export default AboutHero3D;
